import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  FileText,
  Search,
  Settings,
  Eye,
  Edit,
  Download,
  MoreHorizontal,
  RefreshCw,
  Plus,
  Filter,
  Calendar,
  TrendingUp,
  Receipt,
  Wallet,
  CheckCircle2,
  RotateCcw,
  Send,
  FileMinus,
  FileClock,
  FilePen
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import type { Tables } from '../types/database.types';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { DocumentTypeSelectionModal } from '../components/DocumentTypeSelectionModal';
import { InvoiceFormModal } from '../components/documentForms/InvoiceFormModal';
import { ProformaInvoiceFormModal } from '../components/documentForms/ProformaInvoiceFormModal';
import { InvoiceWithDeliveryFormModal } from '../components/documentForms/InvoiceWithDeliveryFormModal';
import { DDTFormModal } from '../components/documentForms/DDTFormModal';
import { CreditNoteFormModal } from '../components/documentForms/CreditNoteFormModal';
import type { DocumentType } from '../lib/invoiceUtils';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { OrdiniDalSito } from '../components/eventi/OrdiniDalSito';
import { InvoicePrintView } from './InvoicePrintView';
import ExportInvoicesModal from '../components/ExportInvoicesModal';
import { AggiornaDaFic } from '../components/AggiornaDaFic';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/cn';
import { anniDiCompetenza, confrontaDocumenti, riepilogoDocumenti, type AnnoScelto } from '../lib/fatture/elencoFatture';
import { COLORI, conta, segnoMeno, type Colore } from '../components/documenti/colori';
import { RiquadroTotale, SceltaAnno, TastoTipo } from '../components/documenti/Riepilogo';

// I tipi presi dalla struttura vera del database: i vecchi `Invoice` e
// `Customer` non esistevano piu', e ogni campo della fattura risultava
// sconosciuto al controllo dei tipi.
type Invoice = Tables<'invoices'>;
type Customer = Tables<'customers'>;

interface InvoiceWithCustomer extends Invoice {
  customers: Pick<Customer, 'company_name'> | null;
}

interface InstallmentRow {
  id: string;
  installment_number: number;
  due_date: string;
  amount: number;
  percentage: number;
  label: string | null;
  payment_status: string;
  paid_at: string | null;
}

/** Valori di stato accettati nel filtro da indirizzo (?status=...). */
const STATUS_FILTERS = ['draft', 'issued', 'cancelled'] as const;

/** Supabase non restituisce piu' di 1000 righe per lettura: si legge a blocchi. */
const BLOCCO_LETTURA = 1000;

/** Il colore della riga secondo il tipo di documento (vedi components/documenti/colori). */
const COLORE_TIPO: Record<string, Colore> = {
  credit_note: 'rosso',
  proforma: 'ambra',
  ddt: 'grigio',
};
const coloreTipo = (tipo: string) => COLORI[COLORE_TIPO[tipo] ?? 'blu'];

export default function InvoicesPage() {
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [flagLoading, setFlagLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);

  const [allInvoices, setAllInvoices] = useState<InvoiceWithCustomer[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit'>('view');
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  const [searchNumber, setSearchNumber] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchParams] = useSearchParams();
  const statusFromUrl = STATUS_FILTERS.find((s) => s === searchParams.get('status')) ?? null;
  const [filterStatus, setFilterStatus] = useState<string>(() => statusFromUrl ?? 'all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFilters, setShowFilters] = useState(() => statusFromUrl !== null);

  // Anno di competenza (data del documento): comanda sia la lista sia i
  // totali. Le bozze di un abbonamento possono avere la data dell'anno
  // prossimo, quindi chi arriva da "Apri bozze" le vede tutte.
  const annoCorrente = new Date().getFullYear();
  const [anno, setAnno] = useState<AnnoScelto>(() => (statusFromUrl === 'draft' ? 'tutti' : annoCorrente));
  const [anni, setAnni] = useState<number[]>([annoCorrente]);
  // L'anno dei documenti davvero in lista: mentre si legge il nuovo anno, le
  // etichette restano su quello dei numeri mostrati.
  const [annoCaricato, setAnnoCaricato] = useState<AnnoScelto>(anno);

  // Il link "Apri bozze" (banner in Dashboard, avviso del mattino) porta qui
  // con ?status=draft. Se si è già su questa pagina il componente non riparte,
  // quindi il filtro va aggiornato a mano.
  useEffect(() => {
    if (!statusFromUrl) return;
    setFilterStatus(statusFromUrl);
    if (statusFromUrl === 'draft') setAnno('tutti');
    setShowFilters(true);
  }, [statusFromUrl]);

  // Il link "Fattura n. ..." della scheda Biglietti di un evento porta qui
  // con ?apri=<id>: si apre subito il dettaglio di quella fattura.
  const apriDaUrl = searchParams.get('apri');
  useEffect(() => {
    if (!apriDaUrl) return;
    setSelectedInvoiceId(apriDaUrl);
    setViewMode('view');
    setShowInvoiceDetail(true);
  }, [apriDaUrl]);
  const [printInvoiceId, setPrintInvoiceId] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  // Dialog scelta scadenze da incassare (fatture con pagamento multiplo)
  const [collectInv, setCollectInv] = useState<InvoiceWithCustomer | null>(null);
  const [collectRows, setCollectRows] = useState<InstallmentRow[]>([]);
  const [collectSel, setCollectSel] = useState<Record<string, boolean>>({});
  const [collectSaving, setCollectSaving] = useState(false);

  // Si leggono i documenti dell'anno scelto, a blocchi; numero, tipo, stato e
  // date si filtrano qui sotto senza tornare al database. Se nel frattempo si
  // cambia anno, la risposta della lettura vecchia si scarta.
  const letturaInCorso = useRef(0);
  const loadInvoices = useCallback(async () => {
    const lettura = ++letturaInCorso.current;
    setListLoading(true);
    const righe: InvoiceWithCustomer[] = [];
    let errore: unknown = null;
    for (;;) {
      let query = supabase
        .from('invoices')
        .select('*, customers(company_name)')
        .order('invoice_date', { ascending: false })
        .order('id')
        .range(righe.length, righe.length + BLOCCO_LETTURA - 1);
      if (anno !== 'tutti') {
        query = query.gte('invoice_date', `${anno}-01-01`).lt('invoice_date', `${anno + 1}-01-01`);
      }
      const { data, error } = await query;
      if (lettura !== letturaInCorso.current) return;
      if (error) { errore = error; break; }
      const blocco = (data ?? []) as InvoiceWithCustomer[];
      righe.push(...blocco);
      if (blocco.length < BLOCCO_LETTURA) break;
    }

    if (errore) {
      // Meglio una lista vuota che i numeri di un altro anno sotto l'etichetta di questo.
      console.error('Error loading invoices:', errore);
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare i documenti' });
      setAllInvoices([]);
    } else {
      // Un documento arrivato fra un blocco e l'altro potrebbe comparire due volte.
      setAllInvoices([...new Map(righe.map((r) => [r.id, r])).values()]);
    }
    setAnnoCaricato(anno);
    setListLoading(false);
  }, [anno, addNotification]);

  // Gli anni da proporre: dal primo documento all'ultimo.
  const loadAnni = useCallback(async () => {
    const [primo, ultimo] = await Promise.all([
      supabase.from('invoices').select('invoice_date').order('invoice_date', { ascending: true }).limit(1).maybeSingle(),
      supabase.from('invoices').select('invoice_date').order('invoice_date', { ascending: false }).limit(1).maybeSingle(),
    ]);
    if (primo.error || ultimo.error) {
      console.error('Error loading years:', primo.error ?? ultimo.error);
      return;
    }
    setAnni(anniDiCompetenza(primo.data?.invoice_date, ultimo.data?.invoice_date, annoCorrente));
  }, [annoCorrente]);

  useEffect(() => {
    if (enabled) {
      loadInvoices();
    }
  }, [enabled, loadInvoices]);

  useEffect(() => {
    if (enabled) {
      loadAnni();
    }
  }, [enabled, loadAnni]);

  const ricarica = useCallback(() => {
    loadInvoices();
    loadAnni();
  }, [loadInvoices, loadAnni]);

  const handleCloseInvoiceDetail = () => {
    setShowInvoiceDetail(false);
    setSelectedInvoiceId(null);
  };

  const togglePaid = async (inv: InvoiceWithCustomer) => {
    const newPaid = inv.payment_status !== 'paid';
    setMarkingId(inv.id);
    const nowIso = new Date().toISOString();

    const { error } = await supabase
      .from('invoices')
      .update({ payment_status: newPaid ? 'paid' : 'pending' })
      .eq('id', inv.id);

    if (!error) {
      // Allinea le scadenze così l'analisi incassi resta coerente
      await supabase
        .from('invoice_installments')
        .update({ payment_status: newPaid ? 'paid' : 'pending', paid_at: newPaid ? nowIso : null })
        .eq('invoice_id', inv.id);
    }

    if (error) {
      addNotification({ type: 'error', title: 'Errore', message: "Impossibile aggiornare l'incasso" });
    } else {
      addNotification({
        type: 'success',
        title: newPaid ? 'Incassata' : 'Riaperta',
        message: newPaid ? 'Fattura segnata come incassata' : 'Fattura segnata come da incassare',
      });
      setAllInvoices((prev) =>
        prev.map((i) => (i.id === inv.id ? { ...i, payment_status: newPaid ? 'paid' : 'pending' } : i))
      );
    }
    setMarkingId(null);
  };

  // Click su "Segna incassata": se la fattura ha più scadenze chiede quali segnare;
  // se ne ha 0 o 1 non c'è nulla da scegliere → segna tutta la fattura come prima.
  const openCollect = async (inv: InvoiceWithCustomer) => {
    setMarkingId(inv.id);
    const { data, error } = await supabase
      .from('invoice_installments')
      .select('id, installment_number, due_date, amount, percentage, label, payment_status, paid_at')
      .eq('invoice_id', inv.id)
      .order('installment_number', { ascending: true });
    setMarkingId(null);

    if (error) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le scadenze' });
      return;
    }

    const rows = (data || []) as InstallmentRow[];
    if (rows.length <= 1) {
      togglePaid(inv);
      return;
    }

    const sel: Record<string, boolean> = {};
    rows.forEach((r) => { sel[r.id] = r.payment_status === 'paid'; });
    setCollectRows(rows);
    setCollectSel(sel);
    setCollectInv(inv);
  };

  const confirmCollect = async () => {
    if (!collectInv) return;
    setCollectSaving(true);
    const nowIso = new Date().toISOString();

    const results = await Promise.all(
      collectRows.map((r) => {
        const paid = !!collectSel[r.id];
        return supabase
          .from('invoice_installments')
          .update({ payment_status: paid ? 'paid' : 'pending', paid_at: paid ? (r.paid_at || nowIso) : null })
          .eq('id', r.id);
      })
    );
    const failed = results.some((r) => r.error);

    const allPaid = collectRows.every((r) => collectSel[r.id]);
    const paidCount = collectRows.filter((r) => collectSel[r.id]).length;

    const { error: invErr } = await supabase
      .from('invoices')
      .update({ payment_status: allPaid ? 'paid' : 'pending' })
      .eq('id', collectInv.id);

    setCollectSaving(false);

    if (failed || invErr) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare gli incassi' });
      return;
    }

    setAllInvoices((prev) =>
      prev.map((i) => (i.id === collectInv.id ? { ...i, payment_status: allPaid ? 'paid' : 'pending' } : i))
    );
    addNotification({
      type: 'success',
      title: allPaid ? 'Incassata' : 'Incasso parziale',
      message: `${paidCount} di ${collectRows.length} scadenze segnate come incassate`,
    });
    setCollectInv(null);
    setCollectRows([]);
    setCollectSel({});
  };

  const getDocumentTypeLabel = (type: string | null | undefined): string => {
    switch (type) {
      case 'invoice': return 'Fattura';
      case 'invoice_with_delivery': return 'Fattura accompagnatoria';
      case 'proforma': return 'Pro forma';
      case 'ddt': return 'DDT';
      case 'credit_note': return 'Nota di credito';
      default: return 'Fattura';
    }
  };

  const loadFlag = useCallback(async () => {
    setFlagLoading(true);
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'invoices_enabled').maybeSingle();
    const val = (data?.value as { enabled?: boolean } | null | undefined)?.enabled;
    setEnabled(val === undefined ? true : Boolean(val));
    setFlagLoading(false);
  }, []);

  useEffect(() => {
    loadFlag();
  }, [loadFlag]);

  const filteredInvoices = useMemo(() => {
    let filtered = [...allInvoices];
    if (searchNumber.trim()) {
      const searchLower = searchNumber.toLowerCase().trim();
      filtered = filtered.filter(inv =>
        inv.invoice_number?.toLowerCase().includes(searchLower) ||
        inv.customers?.company_name?.toLowerCase().includes(searchLower) ||
        (inv.invoice_number === null && searchLower.includes('bozza'))
      );
    }
    if (filterType !== 'all') filtered = filtered.filter(inv => (inv.document_type || 'invoice') === filterType);
    if (filterStatus !== 'all') filtered = filtered.filter(inv => inv.status === filterStatus);
    if (filterDateFrom) filtered = filtered.filter(inv => new Date(inv.invoice_date) >= new Date(filterDateFrom));
    if (filterDateTo) {
      const toDate = new Date(filterDateTo); toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) <= toDate);
    }
    return filtered.sort(confrontaDocumenti);
  }, [allInvoices, searchNumber, filterType, filterStatus, filterDateFrom, filterDateTo]);

  // I totali sommano esattamente i documenti in lista (anno e filtri compresi).
  const riepilogo = useMemo(() => riepilogoDocumenti(filteredInvoices), [filteredInvoices]);

  const activeFiltersCount = [searchNumber, filterType !== 'all', filterStatus !== 'all', filterDateFrom, filterDateTo].filter(Boolean).length;
  const etichettaAnno = annoCaricato === 'tutti' ? 'tutti gli anni' : String(annoCaricato);

  const statusStyles = {
    issued: 'bg-green-500/10 text-green-500 border-green-500/20',
    draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    cancelled: 'bg-red-500/10 text-red-500 border-red-500/20'
  };

  const statusLabels = { issued: 'Emessa', draft: 'Bozza', cancelled: 'Annullata' };

  // Dove vive il documento. Le fatture, le fatture accompagnatorie e le note
  // di credito nascono su Fatture in Cloud, che le numera ed e' da dove
  // partono allo SDI. Quello che resta solo in Pixel (bozze, pro forma, DDT)
  // allo SDI non ci va e non ha un numero di protocollo.
  const renderOrigineBadge = (inv: InvoiceWithCustomer) => {
    const suFic = !!inv.fic_id;
    const cfg = suFic
      ? {
          label: 'Su Fatture in Cloud',
          cls: 'bg-slate-100 text-slate-800 border-slate-300',
          title: 'Numerata da Fatture in Cloud. L’invio allo SDI si fa da Fatture in Cloud.',
        }
      : {
          label: 'Solo in Pixel',
          cls: 'bg-gray-100 text-gray-600 border-gray-300',
          title: 'Documento non presente su Fatture in Cloud: non è numerato e non va allo SDI.',
        };
    return (
      <span
        title={cfg.title}
        className={cn('inline-flex h-7 items-center gap-1 px-2.5 rounded-full border text-[11px] font-semibold whitespace-nowrap', cfg.cls)}
      >
        <Send className="w-3 h-3" />
        <span className="hidden sm:inline">{cfg.label}</span>
      </span>
    );
  };

  // Per le fatture che vivono su Fatture in Cloud l'incasso si segna la', che
  // comanda come per i numeri: qui si legge soltanto. Un "Segna incassata"
  // fatto in Pixel verrebbe riportato indietro dalla sincronizzazione.
  const INCASSO_FIC = {
    paid: { label: 'Incassata', Icona: CheckCircle2, cls: 'bg-green-50 text-green-700 border-green-200' },
    reversed: { label: 'Stornata', Icona: RotateCcw, cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    pending: { label: 'Da incassare', Icona: Wallet, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  } as const;

  const renderIncassoFic = (inv: InvoiceWithCustomer) => {
    const stato = inv.payment_status === 'paid' || inv.payment_status === 'reversed' ? inv.payment_status : 'pending';
    const { label, Icona, cls } = INCASSO_FIC[stato];
    return (
      <span
        title="Incasso letto da Fatture in Cloud: si segna là, qui si aggiorna ogni notte o con “Aggiorna da Fatture in Cloud”."
        className={cn('inline-flex h-7 items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-semibold whitespace-nowrap', cls)}
      >
        <Icona className="w-3.5 h-3.5" />
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Fatture e Documenti
          </h1>
          <p className="text-sm text-neutral-400 mt-1">Gestisci la fatturazione e i documenti commerciali</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AggiornaDaFic ruolo={profile?.role} onAggiornato={ricarica} />
          <Button variant="outline" size="sm" onClick={ricarica} className="h-10">
            <RefreshCw className={cn("w-4 h-4", listLoading && "animate-spin")} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10">
                <Settings className="w-4 h-4 mr-2" /> Azioni
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowExportModal(true)}><Download className="w-4 h-4 mr-2" /> Esporta</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/document-configuration')}><Settings className="w-4 h-4 mr-2" /> Configurazione</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowDocumentTypeModal(true)} className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white flex-1 md:flex-none">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Documento
          </Button>
        </div>
      </div>

      {/* Totali dell'anno di competenza, separati per tipo */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SceltaAnno id="anno-competenza" anno={anno} anni={anni} onCambia={setAnno} />
          <p className="text-xs text-gray-500">
            Importi senza IVA, per data del documento{activeFiltersCount > 0 && ' · con i filtri attivi'}
          </p>
        </div>

        <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-5', listLoading && 'opacity-60 transition-opacity')}>
          {flagLoading ? (
            [...Array(5)].map((_, i) => <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />)
          ) : (
            <>
              <RiquadroTotale
                scuro
                className="col-span-2 lg:col-span-1"
                etichetta={`Fatturato ${etichettaAnno}`}
                icona={TrendingUp}
                coloreIcona="text-green-400"
                valore={formatCurrency(riepilogo.netto.imponibile)}
                nota={`Fatture meno note di credito · ${formatCurrency(riepilogo.netto.totale)} con IVA`}
              />
              <RiquadroTotale
                etichetta="Fatture"
                icona={Receipt}
                coloreIcona="text-blue-600"
                valore={formatCurrency(riepilogo.fatture.imponibile)}
                nota={`${conta(riepilogo.fatture.documenti, 'fattura', 'fatture')} · ${formatCurrency(riepilogo.fatture.totale)} con IVA`}
              />
              <RiquadroTotale
                etichetta="Note di credito"
                icona={FileMinus}
                coloreIcona="text-red-500"
                valore={`${segnoMeno(riepilogo.noteCredito.imponibile)}${formatCurrency(riepilogo.noteCredito.imponibile)}`}
                nota={`${conta(riepilogo.noteCredito.documenti, 'nota', 'note')} · ${segnoMeno(riepilogo.noteCredito.totale)}${formatCurrency(riepilogo.noteCredito.totale)} con IVA`}
              />
              <RiquadroTotale
                etichetta="Pro forma"
                icona={FileClock}
                coloreIcona="text-amber-500"
                valore={formatCurrency(riepilogo.proforma.imponibile)}
                nota={`${conta(riepilogo.proforma.documenti, 'pro forma', 'pro forma')} · fuori dal fatturato · ${formatCurrency(riepilogo.proforma.totale)} con IVA`}
              />
              <RiquadroTotale
                etichetta="Bozze"
                icona={FilePen}
                coloreIcona="text-yellow-500"
                valore={String(riepilogo.bozze)}
                nota="Da completare, non contano nei totali"
                className={cn(riepilogo.bozze > 0 && 'cursor-pointer hover:border-yellow-400')}
                title={riepilogo.bozze > 0 ? 'Mostra solo le bozze' : undefined}
                onClick={() => {
                  if (riepilogo.bozze === 0) return;
                  setFilterStatus('draft');
                  setShowFilters(true);
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Ordini pagati sul sito (biglietti): la stessa fattura della scheda Biglietti */}
      <OrdiniDalSito />

      {/* Search & Filters */}
      <div className="flex flex-col gap-3">
        <Card className="p-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 basis-full sm:basis-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Cerca per numero o cliente..."
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
              />
            </div>
            <TastoTipo
              colore="rosso"
              etichetta="Note di credito"
              acceso={filterType === 'credit_note'}
              onCambia={() => setFilterType(filterType === 'credit_note' ? 'all' : 'credit_note')}
            />
            <TastoTipo
              colore="ambra"
              etichetta="Pro forma"
              acceso={filterType === 'proforma'}
              onCambia={() => setFilterType(filterType === 'proforma' ? 'all' : 'proforma')}
            />
            <Button
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              className={cn("h-10 px-3", showFilters && "bg-gray-100 text-primary-600")}
            >
              <Filter className="w-4 h-4 mr-2" />
              {activeFiltersCount > 0 && <span className="mr-2 px-1.5 py-0.5 rounded-full bg-primary-500 text-white text-[10px]">{activeFiltersCount}</span>}
              Filtri
            </Button>
          </div>
        </Card>

        {showFilters && (
          <Card className="p-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Tipo Documento</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="bg-white border-gray-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="invoice">Fattura</SelectItem>
                    <SelectItem value="invoice_with_delivery">Fattura accompagnatoria</SelectItem>
                    <SelectItem value="credit_note">Nota di credito</SelectItem>
                    <SelectItem value="proforma">Pro forma</SelectItem>
                    <SelectItem value="ddt">DDT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Stato</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-white border-gray-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="issued">Emessa</SelectItem>
                    <SelectItem value="cancelled">Annullata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Data Da</Label>
                <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="bg-white border-gray-200 h-9" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Data A</Label>
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="bg-white border-gray-200 h-9" />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              <Button variant="ghost" size="sm" onClick={() => { setSearchNumber(''); setFilterType('all'); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); }}>Reset</Button>
              <Button size="sm" onClick={() => setShowFilters(false)}>Applica</Button>
            </div>
          </Card>
        )}
      </div>

      {/* Invoices List */}
      <div className="grid grid-cols-1 gap-2">
        {listLoading && allInvoices.length === 0 ? (
          [...Array(6)].map((_, i) => <Card key={i} className="h-16 animate-pulse bg-gray-50 border-gray-100" />)
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12 rounded-xl">
            <FileText className="w-12 h-12 text-neutral-600 mx-auto mb-4 opacity-20" />
            <p className="text-neutral-400">Nessun documento trovato per {etichettaAnno}</p>
            {anno !== 'tutti' && activeFiltersCount > 0 && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setAnno('tutti')}>
                Cerca in tutti gli anni
              </Button>
            )}
          </div>
        ) : (
          filteredInvoices.map((inv) => {
            const isPaid = inv.payment_status === 'paid';
            const docType = inv.document_type || 'invoice';
            const canCollect = inv.status === 'issued' && (docType === 'invoice' || docType === 'invoice_with_delivery');
            const suFic = !!inv.fic_id;
            const isNotaCredito = docType === 'credit_note';
            const cliente = inv.customers?.company_name || 'Cliente non indicato';
            const colore = coloreTipo(docType);
            return (
            <Card
              key={inv.id}
              className={cn('group py-2.5 pl-4 pr-3 sm:pl-5 sm:pr-4 hover:shadow-md transition-shadow duration-200 cursor-pointer bg-white relative overflow-hidden', colore.bordo)}
              onClick={() => { setSelectedInvoiceId(inv.id); setViewMode('view'); setShowInvoiceDetail(true); }}
            >
              <div className={cn('absolute inset-y-0 left-0 w-1', colore.barra)} />

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {/* Numero, tipo e stato */}
                <div className="w-28 shrink-0 sm:w-36">
                  <h3 className="font-bold text-base leading-tight text-gray-900 group-hover:text-primary-600 transition-colors truncate tabular-nums">
                    {inv.invoice_number || <span className="text-neutral-500 italic">Bozza</span>}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={cn('text-[10px] font-semibold uppercase tracking-tight', colore.testo)}>
                      {getDocumentTypeLabel(inv.document_type)}
                    </span>
                    <span className={cn("px-1.5 rounded-full text-[9px] leading-4 font-bold uppercase border", statusStyles[inv.status as keyof typeof statusStyles])}>
                      {statusLabels[inv.status as keyof typeof statusLabels]}
                    </span>
                  </div>
                </div>

                {/* Cliente e data */}
                <div className="min-w-0 flex-1 basis-32 sm:basis-48">
                  <p className="truncate text-[15px] font-semibold leading-tight text-gray-900" title={cliente}>
                    {cliente}
                  </p>
                  <div className="flex items-center gap-x-2 gap-y-1 mt-1 flex-wrap text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      {formatDate(inv.invoice_date)}
                    </span>
                    {inv.deposit_percentage != null && (
                      <span className="text-[10px] px-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-bold uppercase">
                        Acconto {inv.deposit_percentage}%
                      </span>
                    )}
                    {inv.parent_invoice_id && (
                      <span className="text-[10px] px-1.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200 font-bold uppercase">
                        Saldo
                      </span>
                    )}
                    {inv.price_list_id && (
                      <span className="text-[10px] px-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold">
                        da Offerta
                      </span>
                    )}
                  </div>
                </div>

                {/* Fatture in Cloud, incasso, importo e azioni: a destra, o sotto se lo spazio non basta */}
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  {canCollect && (
                    <div className="flex items-center gap-2">
                      {renderOrigineBadge(inv)}
                      {suFic ? renderIncassoFic(inv) : (
                        <Button
                          variant={isPaid ? 'default' : 'outline'}
                          size="sm"
                          disabled={markingId === inv.id}
                          onClick={(e) => { e.stopPropagation(); openCollect(inv); }}
                          className={cn(
                            'h-7 px-3 text-xs rounded-full font-semibold whitespace-nowrap transition-colors',
                            isPaid
                              ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                              : 'border-green-500 text-green-600 hover:bg-green-50'
                          )}
                        >
                          {markingId === inv.id ? (
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : isPaid ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                          ) : (
                            <Wallet className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {isPaid ? 'Incassata' : 'Segna incassata'}
                        </Button>
                      )}
                    </div>
                  )}

                  <div
                    className={cn('text-right text-base font-bold tabular-nums sm:min-w-[6.5rem]', isNotaCredito ? 'text-red-600' : 'text-gray-900')}
                    title={isNotaCredito ? 'Nota di credito: si toglie dal fatturato' : 'Totale del documento, IVA compresa'}
                  >
                    {isNotaCredito && '−'}{formatCurrency(inv.total_amount || 0)}
                  </div>
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hidden h-8 w-8 text-primary-600 hover:bg-slate-50 rounded-full sm:inline-flex"
                      title="Anteprima A4"
                      onClick={(e) => { e.stopPropagation(); setPrintInvoiceId(inv.id); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-neutral-500 hover:bg-gray-100 rounded-full">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setPrintInvoiceId(inv.id); }}><Eye className="w-4 h-4 mr-2" /> Anteprima A4</DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedInvoiceId(inv.id); setViewMode('view'); setShowInvoiceDetail(true); }}><FileText className="w-4 h-4 mr-2" /> Dettaglio</DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedInvoiceId(inv.id); setViewMode('edit'); setShowInvoiceDetail(true); }}><Edit className="w-4 h-4 mr-2" /> Modifica</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </Card>
            );
          })
        )}
      </div>

      <DocumentTypeSelectionModal
        isOpen={showDocumentTypeModal}
        onClose={() => setShowDocumentTypeModal(false)}
        onDocumentTypeSelect={(type) => { setSelectedDocumentType(type); setShowDocumentTypeModal(false); }}
      />

      <InvoiceFormModal isOpen={selectedDocumentType === 'invoice'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); ricarica(); }} />
      <ProformaInvoiceFormModal isOpen={selectedDocumentType === 'proforma'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); ricarica(); }} />
      <InvoiceWithDeliveryFormModal isOpen={selectedDocumentType === 'invoice_with_delivery'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); ricarica(); }} />
      <DDTFormModal isOpen={selectedDocumentType === 'ddt'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); ricarica(); }} />
      <CreditNoteFormModal isOpen={selectedDocumentType === 'credit_note'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); ricarica(); }} />

      <InvoiceDetailPage
        isOpen={showInvoiceDetail}
        onClose={handleCloseInvoiceDetail}
        invoiceId={selectedInvoiceId || undefined}
        mode={viewMode}
        onSaveSuccess={ricarica}
      />

      <ExportInvoicesModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />

      {printInvoiceId && (
        <InvoicePrintView
          isOpen={Boolean(printInvoiceId)}
          onClose={() => setPrintInvoiceId(null)}
          invoiceId={printInvoiceId}
        />
      )}

      <Dialog open={collectInv !== null} onOpenChange={(o) => { if (!o && !collectSaving) { setCollectInv(null); setCollectRows([]); setCollectSel({}); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cosa vuoi segnare incassato?</DialogTitle>
            <DialogDescription>
              {collectInv?.invoice_number ? `Fattura ${collectInv.invoice_number}` : 'Fattura'} — seleziona le scadenze incassate.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {collectRows.map((r) => {
              const checked = !!collectSel[r.id];
              return (
                <label
                  key={r.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    checked ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setCollectSel((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                    className="w-4 h-4 accent-green-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">
                      {r.label || `Scadenza ${r.installment_number}`}
                    </div>
                    <div className="text-xs text-neutral-500">
                      Scadenza {formatDate(r.due_date)} · {r.percentage}%
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gray-900 whitespace-nowrap">
                    {formatCurrency(r.amount)}
                  </div>
                </label>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="ghost" disabled={collectSaving} onClick={() => { setCollectInv(null); setCollectRows([]); setCollectSel({}); }}>
              Annulla
            </Button>
            <Button
              disabled={collectSaving}
              onClick={confirmCollect}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {collectSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
